import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../config/supabase';
import './Layout.css';

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user, role } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isStakeholder = role === 'stakeholder';
  const [stakeholderNameModal, setStakeholderNameModal] = useState(false);
  const [stakeholderName, setStakeholderName] = useState('');
  const [stakeholderNameSaving, setStakeholderNameSaving] = useState(false);
  const [stakeholderNameError, setStakeholderNameError] = useState('');

  const loadStakeholderProfile = useCallback(async () => {
    if (!user?.id || role !== 'stakeholder') return null;
    const { data } = await supabase
      .from('stakeholder_profiles')
      .select('full_name')
      .eq('user_id', user.id)
      .maybeSingle();
    return data;
  }, [user?.id, role]);

  useEffect(() => {
    if (!user?.id || role !== 'stakeholder') return;
    let cancelled = false;
    loadStakeholderProfile().then((data) => {
      if (!cancelled && (!data || !(data.full_name || '').trim())) setStakeholderNameModal(true);
    });
    return () => { cancelled = true; };
  }, [user?.id, role, loadStakeholderProfile]);

  const handleStakeholderNameSubmit = async (e) => {
    e.preventDefault();
    const name = (stakeholderName || '').trim();
    if (!name || !user?.id) return;
    setStakeholderNameError('');
    setStakeholderNameSaving(true);
    try {
      const { error } = await supabase.from('stakeholder_profiles').upsert(
        { user_id: user.id, full_name: name, updated_at: new Date().toISOString() },
        { onConflict: 'user_id' }
      );
      if (error) throw error;
      setStakeholderNameModal(false);
      setStakeholderName('');
    } catch (err) {
      setStakeholderNameError(err?.message || 'Failed to save');
    } finally {
      setStakeholderNameSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  return (
    <div className="layout">
      {isStakeholder && stakeholderNameModal && (
        <div className="layout-stakeholder-name-overlay" role="dialog" aria-modal="true" aria-labelledby="stakeholder-name-title">
          <div className="layout-stakeholder-name-modal">
            <h2 id="stakeholder-name-title">Enter your full name</h2>
            <p className="layout-stakeholder-name-intro">So we know who you are when admins assign you to clients.</p>
            <form onSubmit={handleStakeholderNameSubmit}>
              {stakeholderNameError && <p className="layout-stakeholder-name-error">{stakeholderNameError}</p>}
              <label htmlFor="stakeholder-full-name">Full name</label>
              <input
                id="stakeholder-full-name"
                type="text"
                value={stakeholderName}
                onChange={(e) => setStakeholderName(e.target.value)}
                placeholder="e.g. Jane Smith"
                required
                autoFocus
                disabled={stakeholderNameSaving}
              />
              <button type="submit" className="layout-stakeholder-name-submit" disabled={stakeholderNameSaving || !(stakeholderName || '').trim()}>
                {stakeholderNameSaving ? 'Saving…' : 'Continue'}
              </button>
            </form>
          </div>
        </div>
      )}
      <header className="dashboard-header">
        <div className="header-container">
          <button className="burger-menu" onClick={toggleSidebar}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <Link to="/" className="logo">
            <h1>MarketVendr Dashboard</h1>
          </Link>
          <div className="header-user-section">
            {user && (
              <span className="welcome-text">Welcome, {user.email}</span>
            )}
            <button onClick={handleLogout} className="logout-button">
              Logout
            </button>
          </div>
        </div>
      </header>
      
      <div className="dashboard-container">
        {/* Sidebar Overlay for mobile */}
        {sidebarOpen && <div className="sidebar-overlay" onClick={closeSidebar}></div>}
        
        {/* Sidebar */}
        <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
          <nav className="sidebar-nav">
            <Link
              to="/"
              className={`sidebar-link ${location.pathname === '/' ? 'active' : ''}`}
              onClick={closeSidebar}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              <span>Home</span>
            </Link>
            {!isStakeholder && (
              <>
                <Link
                  to="/vendors"
                  className={`sidebar-link ${location.pathname.startsWith('/vendors') ? 'active' : ''}`}
                  onClick={closeSidebar}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <span>Vendrs</span>
                </Link>
                <Link
                  to="/website"
                  className={`sidebar-link ${location.pathname.startsWith('/website') ? 'active' : ''}`}
                  onClick={closeSidebar}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Website</span>
                </Link>
                <Link
                  to="/analytics"
                  className={`sidebar-link ${location.pathname.startsWith('/analytics') ? 'active' : ''}`}
                  onClick={closeSidebar}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <span>Analytics</span>
                </Link>
                <Link
                  to="/import-templates"
                  className={`sidebar-link ${location.pathname.startsWith('/import-templates') ? 'active' : ''}`}
                  onClick={closeSidebar}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Import templates</span>
                </Link>
              </>
            )}
            <Link
              to="/clients"
              className={`sidebar-link ${location.pathname.startsWith('/clients') ? 'active' : ''}`}
              onClick={closeSidebar}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <span>Clients</span>
            </Link>
            {!isStakeholder && (
              <Link
                to="/stakeholders"
                className={`sidebar-link ${location.pathname.startsWith('/stakeholders') ? 'active' : ''}`}
                onClick={closeSidebar}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                <span>Stakeholders</span>
              </Link>
            )}
            {!isStakeholder && (
              <>
                <div className="sidebar-divider"></div>
                <Link
                  to="/settings"
                  className={`sidebar-link ${location.pathname.startsWith('/settings') ? 'active' : ''}`}
                  onClick={closeSidebar}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>Settings</span>
                </Link>
              </>
            )}
          </nav>
        </aside>
        
        {/* Main Content */}
        <main className="dashboard-main">{children}</main>
      </div>
    </div>
  );
};

export default Layout;

